/**
 * KPI metric card — shown in the Cards view.
 * Left accent border color = RAG status.
 * Shows: metric label, value, unit, sparkline, target label, delta.
 * Clicking opens the MetricDrawer.
 *
 * Reliability extension: the three gated-out statuses (insufficient_data /
 * not_available / not_tracked) render as a distinct NEUTRAL chip — never
 * red or green — with the gate_reason as a caption/tooltip. Gated-in (RAG)
 * metrics show a small "measured/population" coverage caption, and a
 * "ramping" annotation when the metric's window is still building history.
 */
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import type { ReactElement } from 'react';

import type { KpiStatus, Metric } from '@/types/scorecard';
import { isNeutralStatus } from '@/types/scorecard';
import { Sparkline } from './Sparkline';

interface KpiCardProps {
  metric: Metric;
  onClick: (metric: Metric) => void;
}

const STATUS_COLOR: Record<string, string> = {
  green: '#6BFFAB',
  amber: '#FFB000',
  red: '#ef4444',
  insufficient_data: '#8a8a94',
  not_available: '#8a8a94',
  not_tracked: '#8a8a94',
  na: '#6c6c78',
};

const NEUTRAL_LABEL: Record<string, string> = {
  insufficient_data: 'Insufficient data',
  not_available: 'Field missing in Notion',
  not_tracked: 'Not yet tracked',
};

const DELTA_ARROW: Record<string, string> = {
  good: '▲',
  bad: '▼',
  flat: '',
};

function statusChipLabel(status: KpiStatus): string {
  return NEUTRAL_LABEL[status] ?? status.toUpperCase();
}

export function KpiCard({ metric, onClick }: KpiCardProps): ReactElement {
  const accentColor = STATUS_COLOR[metric.status] ?? '#6c6c78';
  const isNa = metric.status === 'na';
  const isNeutral = isNeutralStatus(metric.status);
  const isGated = metric.reliability.gated;

  return (
    <Box
      onClick={() => onClick(metric)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(metric); }}
      aria-label={
        isNeutral
          ? `${metric.label}: ${statusChipLabel(metric.status)}${metric.reliability.gate_reason ? `. ${metric.reliability.gate_reason}` : ''}. Click to see detail.`
          : `${metric.label}: ${metric.display}. Click to see detail.`
      }
      sx={{
        position: 'relative',
        border: '1px solid',
        borderLeft: '3px solid',
        borderColor: 'divider',
        borderLeftColor: accentColor,
        borderRadius: '6px',
        background: 'background.elev2',
        p: '15px 16px 13px',
        cursor: 'pointer',
        minHeight: 44,
        transition: 'border-color 160ms, transform 120ms, box-shadow 160ms',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: '#3a3a44',
          boxShadow: '0 6px 20px rgba(0,0,0,.45)',
        },
        '&:focus-visible': {
          outline: `2px solid ${accentColor}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* Header row: label + status dot */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.125 }}>
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.35,
            flex: 1,
            pr: 1,
          }}
        >
          {metric.label}
        </Typography>
        <Tooltip
          title={metric.reliability.gate_reason ?? metric.na_reason ?? statusChipLabel(metric.status)}
          placement="top"
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: accentColor,
              boxShadow: isNa || isNeutral ? 'none' : `0 0 6px ${accentColor}70`,
              flexShrink: 0,
            }}
            aria-label={`Status: ${metric.status}`}
          />
        </Tooltip>
      </Stack>

      {/* Value */}
      {isNeutral ? (
        <Tooltip title={metric.reliability.gate_reason ?? ''} placement="top" disableHoverListener={!metric.reliability.gate_reason}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
              color: '#8a8a94',
              bgcolor: 'rgba(138,138,148,0.10)',
              border: '1px solid rgba(138,138,148,0.30)',
              borderRadius: '4px',
              px: 1,
              py: 0.5,
              minHeight: 28,
            }}
          >
            {statusChipLabel(metric.status)}
          </Box>
        </Tooltip>
      ) : isNa ? (
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 600,
            fontSize: '1.05rem',
            color: 'text.disabled',
            lineHeight: 1,
          }}
        >
          {metric.na_reason ?? 'n/a'}
        </Typography>
      ) : (
        <Stack direction="row" alignItems="baseline" spacing={0.625}>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 700,
              fontSize: '1.9rem',
              lineHeight: 1,
              letterSpacing: '-0.5px',
              color: 'text.primary',
            }}
          >
            {metric.display}
          </Typography>
          {metric.unit && (
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: 'text.disabled',
                fontWeight: 500,
                letterSpacing: 0,
              }}
            >
              {metric.unit}
            </Typography>
          )}
        </Stack>
      )}

      {/* Sparkline */}
      <Box sx={{ my: 1.375, display: 'block', width: '100%', height: 34 }}>
        <Sparkline
          data={metric.spark}
          status={metric.status}
          height={34}
          targetLine={metric.target?.prorated ?? metric.target?.value ?? null}
          ariaLabel={`${metric.label} trend sparkline`}
        />
      </Box>

      {/* Coverage caption (gated-in RAG metrics) + ramping annotation */}
      {(!isNeutral && !isNa && !isGated) || metric.ramping ? (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
          {!isNeutral && !isNa && !isGated && (
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.625rem',
                color: 'text.faintest',
              }}
            >
              {metric.reliability.measured}/{metric.reliability.population} measured
            </Typography>
          )}
          {metric.ramping && (
            <Tooltip title="Window still building history since the 14-Jun cutoff — RAG shown, but treat as provisional." placement="top">
              <Box
                component="span"
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.5625rem',
                  fontWeight: 700,
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: '3px',
                  px: 0.5,
                  py: '1px',
                }}
              >
                Ramping
              </Box>
            </Tooltip>
          )}
        </Stack>
      ) : null}

      {/* Footer: target + delta */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem',
            color: 'text.disabled',
          }}
        >
          tgt{' '}
          <Box component="b" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {metric.target.label}
          </Box>
        </Typography>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.6875rem',
            fontWeight: 600,
            color:
              metric.delta.kind === 'good'
                ? '#6BFFAB'
                : metric.delta.kind === 'bad'
                ? '#ef4444'
                : 'text.disabled',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
          }}
        >
          {DELTA_ARROW[metric.delta.kind] && (
            <Box component="span" aria-hidden="true">
              {DELTA_ARROW[metric.delta.kind]}
            </Box>
          )}
          {metric.delta.display}
        </Typography>
      </Stack>
    </Box>
  );
}

/** Skeleton placeholder while the scorecard is loading. */
export function KpiCardSkeleton(): ReactElement {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderLeft: '3px solid',
        borderColor: 'divider',
        borderLeftColor: 'divider',
        borderRadius: '6px',
        background: 'background.elev2',
        p: '15px 16px',
        height: 148,
      }}
    >
      <Skeleton variant="text" width="60%" height={14} sx={{ mb: 1.375 }} />
      <Skeleton variant="text" width="35%" height={30} sx={{ mb: 1.375 }} />
      <Skeleton variant="rectangular" width="100%" height={34} sx={{ borderRadius: '3px', mb: 1.375 }} />
      <Skeleton variant="text" width="80%" height={12} />
    </Box>
  );
}
